<template>
  <div v-loading="loadingPDF" class="pdf-preview" :class="{ 'pdf-preview--full': isFull }">
    <header v-if="title" class="pdf-preview__header">
      <h1 class="pdf-preview__title">{{ title }}</h1>
    </header>

    <div ref="pdfContainer" class="pdf-preview__container" @scroll="onScroll">
      <div
        v-for="(item, index) in loadedPages"
        :key="item.page"
        ref="canvasbody"
        class="pdf-preview__page"
        :style="getPageStyle(item)"
      >
        <canvas
          :id="'pdf-canvas-' + index"
          :ref="'canvas-' + item.page"
          class="pdf-preview__canvas"
          @contextmenu.prevent
        />
        <div v-if="!item.rendered" class="pdf-preview__page-placeholder">
          第 {{ item.page }} 页加载中...
        </div>
      </div>
      <div v-if="!loadedPages.length && loadingPDF" class="pdf-preview__loading-text">
        正在加载 PDF...
      </div>
    </div>

    <div class="pdf-preview__toolbar-wrap">
      <div class="pdf-preview__toolbar" :class="{ 'pdf-preview__toolbar--float': floating && !isFull }">
        <button type="button" class="pdf-preview__tool-btn" title="放大" @click="zoomIn">
          <i class="el-icon-zoom-in" />
        </button>
        <button type="button" class="pdf-preview__tool-btn" title="缩小" @click="zoomOut">
          <i class="el-icon-zoom-out" />
        </button>
        <button type="button" class="pdf-preview__tool-btn" title="全屏" @click="toggleFull">
          <i :class="isFull ? 'el-icon-copy-document' : 'el-icon-full-screen'" />
        </button>
        <div class="pdf-preview__page-input">
          <input
            type="text"
            :value="showPage"
            @keyup.enter="goToPage"
            @input="inputChange"
          />
          <span>/ {{ totalPages }}</span>
        </div>
        <button type="button" class="pdf-preview__page-btn" @click="prevPage">上一页</button>
        <button type="button" class="pdf-preview__page-btn" @click="nextPage">下一页</button>
      </div>
    </div>
  </div>
</template>

<script>
import pdfJS from 'pdfjs-dist/webpack'
import { mapState } from 'vuex'

const WATERMARK_BASE = '中国民族工商文化特色资源库'
const PAGE_GAP = 10
const PRELOAD_RANGE = 2

export default {
  name: 'PDFPreview',
  props: {
    page: {
      type: Number,
      default: 1
    },
    pdfUrl: {
      type: String,
      required: true
    },
    title: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      loadingPDF: true,
      floating: true,
      isFull: false,
      defaultPageWidth: 0,
      defaultPageHeight: 0,
      showPage: 1,
      jumpPage: 0,
      pdfDoc: null,
      totalPages: 0,
      scale: 1,
      loadedPages: [],
      loadingPages: new Set(),
      jumpingPage: false
    }
  },
  computed: {
    ...mapState('user', ['userInfo'])
  },
  mounted() {
    this.initPdf()
  },
  beforeDestroy() {
    document.body.style.overflow = ''
  },
  methods: {
    getPageStyle(item) {
      const width = item.width || this.defaultPageWidth || 600
      const height = item.height || this.defaultPageHeight || 800
      return {
        width: `${width}px`,
        height: `${height}px`
      }
    },
    onScroll() {
      if (this.jumpingPage) return

      const container = this.$refs.pdfContainer
      const bodies = this.$refs.canvasbody
      if (!container || !bodies || !bodies.length) return

      const scrollMiddle = container.scrollTop + container.clientHeight / 2
      let current = 1
      for (let i = 0; i < bodies.length; i += 1) {
        const top = bodies[i].offsetTop
        const bottom = top + bodies[i].offsetHeight
        if (scrollMiddle >= top && scrollMiddle < bottom + PAGE_GAP) {
          current = i + 1
          break
        }
        if (scrollMiddle >= bottom) {
          current = i + 1
        }
      }

      if (current !== this.showPage) {
        this.showPage = current
      }

      const maxScroll = container.scrollHeight - container.clientHeight
      this.floating = container.scrollTop < maxScroll - 8
      this.preloadAround(current)
    },
    async preloadAround(pageNum) {
      const start = Math.max(1, pageNum - PRELOAD_RANGE)
      const end = Math.min(this.totalPages, pageNum + PRELOAD_RANGE)
      const tasks = []
      for (let page = start; page <= end; page += 1) {
        tasks.push(this.renderPageIfNeeded(page))
      }
      await Promise.all(tasks)
    },
    async initPdf() {
      if (!this.pdfUrl) {
        this.loadingPDF = false
        return
      }
      try {
        this.loadingPDF = true
        this.pdfDoc = await pdfJS.getDocument({
          url: this.pdfUrl,
          rangeChunkSize: 65536
        }).promise

        this.totalPages = this.pdfDoc.numPages
        this.showPage = 1
        this.loadedPages = Array.from({ length: this.totalPages }, (_, index) => ({
          page: index + 1,
          width: 0,
          height: 0,
          rendered: false,
          scale: 0
        }))

        await this.renderPageIfNeeded(1)
        const firstPage = this.loadedPages[0]
        if (firstPage) {
          this.defaultPageWidth = firstPage.width
          this.defaultPageHeight = firstPage.height
        }

        await this.preloadAround(this.page > 1 ? this.page : 1)
        if (this.page > 1) {
          this.jumpPage = this.page
          await this.goToPage()
        } else {
          await this.$nextTick()
          this.onScroll()
        }
      } catch (error) {
        console.error('加载 PDF 失败:', error)
        this.$message.error('PDF 加载失败，请稍后重试')
      } finally {
        this.loadingPDF = false
      }
    },
    shouldRenderPage(pageNum) {
      const pageState = this.loadedPages[pageNum - 1]
      if (!pageState) return false
      return !pageState.rendered || pageState.scale !== this.scale
    },
    async renderPageIfNeeded(pageNum) {
      if (!this.shouldRenderPage(pageNum) || this.loadingPages.has(pageNum)) {
        return
      }

      this.loadingPages.add(pageNum)
      try {
        await this.renderPage(pageNum)
      } finally {
        this.loadingPages.delete(pageNum)
      }
    },
    async renderPage(pageNum) {
      const page = await this.pdfDoc.getPage(parseInt(pageNum, 10))
      const viewport = page.getViewport(this.scale)
      const outputScale = window.devicePixelRatio || 1
      const width = Math.floor(viewport.width)
      const height = Math.floor(viewport.height)

      await this.$nextTick()
      const canvas = document.getElementById(`pdf-canvas-${pageNum - 1}`)
      if (!canvas) return

      const context = canvas.getContext('2d')
      canvas.width = Math.floor(viewport.width * outputScale)
      canvas.height = Math.floor(viewport.height * outputScale)
      canvas.style.width = `${width}px`
      canvas.style.height = `${height}px`

      const transform = outputScale !== 1 ? [outputScale, 0, 0, outputScale, 0, 0] : null
      await page.render({
        canvasContext: context,
        transform,
        viewport
      }).promise

      this.renderWatermark(pageNum)

      const pageState = this.loadedPages[pageNum - 1]
      this.$set(pageState, 'width', width)
      this.$set(pageState, 'height', height)
      this.$set(pageState, 'rendered', true)
      this.$set(pageState, 'scale', this.scale)

      if (!this.defaultPageWidth || !this.defaultPageHeight) {
        this.defaultPageWidth = width
        this.defaultPageHeight = height
      }
    },
    createWatermarkPattern() {
      const user = this.userInfo
      let waterText = WATERMARK_BASE
      if (user && user.userName) {
        waterText += user.userName
        if (user.userAccount || user.userEmail) {
          waterText += user.userAccount || user.userEmail
        }
      }
      const canvas = document.createElement('canvas')
      const ctx = canvas.getContext('2d')
      ctx.font = '14px sans-serif'
      const textWidth = ctx.measureText(waterText).width
      const rotationAngle = (-15 * Math.PI) / 180
      const textHeight = 20
      const rotatedWidth =
        Math.abs(Math.cos(rotationAngle) * textWidth) +
        Math.abs(Math.sin(rotationAngle) * textHeight)
      const rotatedHeight =
        Math.abs(Math.sin(rotationAngle) * textWidth) +
        Math.abs(Math.cos(rotationAngle) * textHeight)

      canvas.width = rotatedWidth * 1.05
      canvas.height = rotatedHeight * 1.1

      const context = canvas.getContext('2d')
      context.translate(canvas.width / 2, canvas.height / 2)
      context.rotate(rotationAngle)
      context.font = '14px sans-serif'
      context.fillStyle = 'rgba(107, 90, 78, 0.18)'
      context.textAlign = 'center'
      context.textBaseline = 'middle'
      context.fillText(waterText, 0, 0)
      return canvas
    },
    renderWatermark(pageNum) {
      const ref = this.$refs[`canvas-${pageNum}`]
      const canvas = Array.isArray(ref) ? ref[0] : ref
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const pattern = ctx.createPattern(this.createWatermarkPattern(), 'repeat')
      ctx.rect(0, 0, canvas.width, canvas.height)
      ctx.fillStyle = pattern
      ctx.fill()
    },
    async prevPage() {
      if (this.showPage <= 1) return
      await this.goToPageNumber(this.showPage - 1)
    },
    async nextPage() {
      if (this.showPage >= this.totalPages) return
      await this.goToPageNumber(this.showPage + 1)
    },
    async zoomIn() {
      if (this.scale >= 2) return
      this.scale = Math.round((this.scale + 0.1) * 10) / 10
      await this.resetAndRenderCurrent()
    },
    async zoomOut() {
      if (this.scale <= 0.4) return
      this.scale = Math.round((this.scale - 0.1) * 10) / 10
      await this.resetAndRenderCurrent()
    },
    async resetAndRenderCurrent() {
      this.loadedPages = this.loadedPages.map((item) => ({
        ...item,
        rendered: false,
        scale: 0
      }))
      this.loadingPages.clear()
      this.defaultPageWidth = 0
      this.defaultPageHeight = 0
      await this.renderPageIfNeeded(this.showPage)
      const current = this.loadedPages[this.showPage - 1]
      if (current) {
        this.defaultPageWidth = current.width
        this.defaultPageHeight = current.height
      }
      await this.preloadAround(this.showPage)
      await this.scrollToPage(this.showPage)
    },
    async scrollToPage(pageNum) {
      const container = this.$refs.pdfContainer
      if (!container) return

      this.jumpingPage = true
      await this.$nextTick()
      await new Promise((resolve) => requestAnimationFrame(resolve))

      const bodies = this.$refs.canvasbody
      if (!bodies || !bodies[pageNum - 1]) {
        this.jumpingPage = false
        return
      }

      const pageEl = bodies[pageNum - 1]
      container.scrollTop = Math.max(0, pageEl.offsetTop - PAGE_GAP)

      window.setTimeout(() => {
        this.jumpingPage = false
        this.showPage = pageNum
        this.preloadAround(pageNum)
      }, 200)
    },
    inputChange(e) {
      this.jumpPage = Number(e.target.value)
    },
    async goToPage() {
      const target = Number(this.jumpPage)
      if (!target || target < 1 || target > this.totalPages) return
      await this.goToPageNumber(target)
      this.jumpPage = 0
    },
    async goToPageNumber(pageNum) {
      this.showPage = pageNum
      await this.preloadAround(pageNum)
      await this.scrollToPage(pageNum)
    },
    toggleFull() {
      this.isFull = !this.isFull
      document.body.style.overflow = this.isFull ? 'hidden' : ''
    }
  }
}
</script>

<style lang="scss" scoped>
.pdf-preview {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: $color-cream;

  &--full {
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: $color-white;
  }

  &__header {
    flex-shrink: 0;
    padding: $spacing-base $spacing-xl;
    background: $color-white;
    border-bottom: 1px solid rgba($color-border, 0.35);
  }

  &__title {
    font-family: $font-serif;
    font-size: rem(18);
    font-weight: 700;
    color: $color-brown-dark;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__container {
    flex: 1;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    padding: $spacing-xl 0 120px;
    background: #fafafa;
    scrollbar-width: thin;
    scrollbar-color: $success-color rgba($color-border, 0.3);

    &::-webkit-scrollbar {
      width: 6px;
    }

    &::-webkit-scrollbar-thumb {
      background: $success-color;
      border-radius: 3px;
    }

    &::-webkit-scrollbar-track {
      background: rgba($color-border, 0.25);
    }
  }

  &__page {
    position: relative;
    margin: 0 auto 10px;
    box-shadow: 0 4px 20px rgba($color-brown-dark, 0.12);
    background: $color-white;
  }

  &__canvas {
    display: block;
    margin: 0 auto;
    object-fit: contain;
  }

  &__page-placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    color: $color-text-light;
    font-size: rem(14);
    background: rgba($color-cream, 0.85);
    pointer-events: none;
  }

  &__loading-text {
    padding: $spacing-4xl;
    text-align: center;
    color: $color-text-light;
    font-size: rem(15);
  }

  &__toolbar-wrap {
    flex-shrink: 0;
    height: 72px;
  }

  &__toolbar {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: $spacing-sm;
    padding: $spacing-md $spacing-base;
    background: $color-white;
    border-top: 1px solid rgba($color-border, 0.35);
    box-shadow: 0 -4px 16px rgba($color-brown-dark, 0.06);

    &--float {
      position: fixed;
      left: 0;
      right: 0;
      bottom: 0;
      z-index: 100;
    }
  }

  &--full &__toolbar {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba($color-white, 0.96);
  }

  &--full &__container {
    position: absolute;
    inset: 0 0 72px;
    padding-bottom: $spacing-xl;
  }

  &__tool-btn,
  &__page-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 36px;
    height: 36px;
    padding: 0 12px;
    border: 1px solid rgba($color-border, 0.6);
    border-radius: 4px;
    background: #fafafa;
    color: $color-text;
    font-size: rem(14);
    transition: color 0.2s, border-color 0.2s, background 0.2s;

    &:hover {
      color: $success-color;
      border-color: $success-color;
      background: rgba($success-color, 0.06);
    }

    i {
      font-size: rem(18);
    }
  }

  &__page-input {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    height: 36px;
    padding: 0 10px;
    border: 1px solid rgba($color-border, 0.6);
    border-radius: 4px;
    background: #fafafa;
    margin: 0 $spacing-xs;

    input {
      width: 36px;
      height: 24px;
      border: none;
      background: rgba($color-paper-light, 0.8);
      border-radius: 2px;
      text-align: center;
      font-size: rem(14);
      font-weight: 600;
      color: $color-text;
      outline: none;
    }

    span {
      font-size: rem(14);
      color: $color-text-light;
      white-space: nowrap;
    }
  }
}
</style>
